/**
 * Currency Model - Représente une devise avec son taux de change
 * @class Currency
 */
export class Currency {
    /**
     * Crée une instance de Currency
     * @param {Object} data - Données de la devise
     */
    constructor(data = {}) {
        this._code = data.code || '';
        this._name = data.name || '';
        this._symbol = data.symbol || '';
        this._rate = data.rate || 1;
        this._baseCurrency = data.baseCurrency || 'EUR';
    }

    // Getters
    get code() { return this._code; }
    get name() { return this._name; }
    get symbol() { return this._symbol; }
    get rate() { return this._rate; }
    get baseCurrency() { return this._baseCurrency; }

    // Setters
    set code(value) { this._code = value; }
    set name(value) { this._name = value; }
    set symbol(value) { this._symbol = value; }
    set rate(value) { this._rate = value; }
    set baseCurrency(value) { this._baseCurrency = value; }

    /**
     * Convertit un montant de la devise de base vers cette devise
     * @param {number} amount - Montant à convertir
     * @returns {number} Montant converti
     */
    convert(amount) {
        return amount * this._rate;
    }

    /**
     * Convertit un montant de cette devise vers la devise de base
     * @param {number} amount - Montant à convertir
     * @returns {number} Montant converti
     */
    convertToBase(amount) {
        return amount / this._rate;
    }

    /**
     * Formate un montant dans cette devise
     * @param {number} amount - Montant à formater
     * @param {string} locale - Locale pour le formatage
     * @returns {string} Montant formaté
     */
    format(amount, locale = 'fr-FR') {
        try {
            return new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: this._code,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(amount);
        } catch (e) {
            // Fallback si le code de devise n'est pas supporté
            return `${amount.toFixed(2)} ${this._symbol || this._code}`;
        }
    }

    /**
     * Crée une instance Currency à partir des données d'un pays
     * @param {Object} currencyData - Données de devise du pays
     * @param {number} rate - Taux de change
     * @param {string} baseCurrency - Devise de base
     * @returns {Currency} Instance de Currency
     */
    static fromCountryData(currencyData, rate = 1, baseCurrency = 'EUR') {
        return new Currency({
            code: currencyData.code,
            name: currencyData.name,
            symbol: currencyData.symbol,
            rate: rate,
            baseCurrency: baseCurrency
        });
    }

    /**
     * Convertit l'instance en objet JSON
     * @returns {Object} Représentation JSON
     */
    toJSON() {
        return {
            code: this._code,
            name: this._name,
            symbol: this._symbol,
            rate: this._rate,
            baseCurrency: this._baseCurrency
        };
    }
}
